import { useEffect, useRef, useCallback } from 'react';

interface TurnstileProps {
  siteKey: string;
  onVerify: (token: string) => void;
  theme?: 'light' | 'dark';
  className?: string;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: HTMLElement | string, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export function Turnstile({ siteKey, onVerify, theme = 'light', className = '' }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string>();
  const scriptLoaded = useRef(false);

  const resetWidget = useCallback(() => {
    if (widgetId.current && window.turnstile) {
      window.turnstile.reset(widgetId.current);
    }
  }, []);

  const removeWidget = useCallback(() => {
    if (widgetId.current && window.turnstile) {
      try {
        window.turnstile.remove(widgetId.current);
      } catch (error) {
        console.warn('Error al remover widget de Turnstile:', error);
      }
      widgetId.current = undefined;
    }
  }, []);

  const renderTurnstile = useCallback(() => {
    if (!containerRef.current || !window.turnstile || widgetId.current) return;

    try {
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token: string) => {
          onVerify(token);
        },
        theme: theme,
        'refresh-expired': 'auto'
      });
    } catch (error) {
      console.error('Error al renderizar Turnstile:', error);
    }
  }, [siteKey, onVerify, theme]);

  useEffect(() => {
    if (scriptLoaded.current) {
      renderTurnstile();
      return;
    }

    const existingScript = document.querySelector('script#cf-turnstile');
    if (existingScript) {
      scriptLoaded.current = true;
      renderTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.id = 'cf-turnstile';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;

    window.onTurnstileLoad = () => {
      scriptLoaded.current = true;
      renderTurnstile();
    };

    document.head.appendChild(script);

    return () => {
      removeWidget();
    };
  }, [renderTurnstile, removeWidget]);

  // Limpiar el widget cuando el componente se desmonta
  useEffect(() => {
    return () => {
      removeWidget();
      if (window.onTurnstileLoad) {
        window.onTurnstileLoad = undefined;
      }
    };
  }, [removeWidget]);

  return (
    <div 
      ref={containerRef} 
      className={`flex justify-center items-center min-h-[65px] ${className}`}
      data-theme={theme}
    >
      {!scriptLoaded.current && (
        <div className="text-sm text-gray-500">Cargando captcha...</div>
      )}
    </div>
  );
} 